import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireSessionUser } from '@/integrations/supabase/session';
import { useTenant } from '@/hooks/useTenant';
import { toast } from 'sonner';

interface CompanyInviteParams {
  email: string;
  companyName: string;
  companyDetails?: {
    abn?: string;
    phone?: string;
    email?: string;
    street_address?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    /** Canonical country name (drives tax defaults + timezone in handle_new_user) */
    country?: string;
    /** IANA timezone; defaults to the country's main zone */
    timezone?: string;
  };
}

export function useCompanyInvites() {
  const queryClient = useQueryClient();
  const { me } = useTenant();

  const { data: companyInvites = [], isLoading, refetch } = useQuery({
    queryKey: ['company-invites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_invites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const createCompanyInvite = useMutation({
    mutationFn: async (params: CompanyInviteParams) => {
      await requireSessionUser();

      // Create the invite via RPC
      const { data, error } = await supabase.rpc('invite_company_founder', {
        p_email: params.email,
        p_company_name: params.companyName,
        p_company_details: params.companyDetails || {},
      });

      if (error) throw error;

      // Send the invite email
      let emailSent = false;
      try {
        console.log('📨 Sending company founder invite email to:', params.email);
        const emailResult = await supabase.functions.invoke('send-company-invite-email', {
          body: {
            email: params.email,
            inviteToken: data[0].invite_token,
            companyName: params.companyName,
            invitedByName: me?.fullName || 'System Administrator',
          },
        });

        if (emailResult.error) {
          console.error('❌ Email function error:', emailResult.error);
        } else {
          console.log('✅ Company invite email sent successfully');
          emailSent = true;
        }
      } catch (emailError) {
        console.error('❌ Failed to send company invite email:', emailError);
      }

      return { data, emailSent };
    },
    onSuccess: ({ emailSent }) => {
      queryClient.invalidateQueries({ queryKey: ['company-invites'] });
      if (emailSent) {
        toast.success('Company founder invite sent successfully');
      } else {
        toast.warning('Invite created but email failed to send — check Supabase edge function logs');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send company invite');
    },
  });

  const revokeCompanyInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from('company_invites')
        .update({ status: 'revoked', updated_at: new Date().toISOString() })
        .eq('id', inviteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-invites'] });
      toast.success('Company invite revoked');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to revoke invite');
    },
  });

  return {
    companyInvites,
    isLoading,
    createCompanyInvite,
    revokeCompanyInvite,
    refetch,
  };
}
